import type { CartOrderForm, CartOrderSummary, CartOrderSummaryItem, CheckoutsOrdersRequest, EventPromoCodeItem, EventTicketsInventoryItem, EventTicketsInventoryItemResponse, UserCheckoutOptionsInfo, UserCheckoutOptionsResponse, UserName } from '@/types/checkout';
import type { FormattedUserProfile } from '@/types/profile';

export const transformTicketsInventoryList = (items: EventTicketsInventoryItemResponse[], ticketsPromotionCode: EventPromoCodeItem | null): EventTicketsInventoryItem[] => {
	const firstPromotionCodeTickets = ticketsPromotionCode?.tickets;

	// Create mapping discount price with ticket id
	const mappingDiscountTicket = new Map();
	if (firstPromotionCodeTickets?.length) {
		firstPromotionCodeTickets.forEach((item) => {
			if (item.price !== item.discountedPrice) {
				mappingDiscountTicket.set(item.id, item.discountedPrice);
			}
		});
	}

	return items.map((item) => {
		// Get discount price
		const discountPrice = mappingDiscountTicket.get(item.id) ?? null;
		return {
			id: item.id,
			name: item.name,
			status: item.status,
			seatsMax: item.seatsMax,
			seatsSold: item.seatsSold,
			seatsAvailable: item.seatsMax - item.seatsSold,
			price: item.price,
			discountPrice,
			currency: item.currency,
			requiresTokenGates: item.requiresTokenGates,
		};
	});
};


export const transformCartToOrderSummary = (cartItems: CartOrderForm[]): CartOrderSummary => {
	return cartItems.reduce((result, item) => {
		if (item.quantity > 0) {
			const sumOfOriginalPrice = item.price * item.quantity;
			const sumOfDiscountPrice = item.discountPrice * item.quantity;
			result.cartItems.push({
				id: item.id,
				name: item.name,
				total: sumOfOriginalPrice,
				totalDiscount: item.discountPrice === null ? null : sumOfDiscountPrice,
				quantity: item.quantity,
				currency: item.currency,
			});
			// Check if item has discount price and apply total with discount price
			if (item.discountPrice !== null && item.price > item.discountPrice) {
				result.total += sumOfDiscountPrice;
			} else {
				result.total += sumOfOriginalPrice;
			}
		}
		return result;
	}, {
		cartItems: [] as CartOrderSummaryItem[],
		total: 0,
	});
};

export const transformUserCheckoutOptions = (data: UserCheckoutOptionsResponse): UserCheckoutOptionsInfo => {
	const cards = data.paymentCards.map((card) => {
		return {
			...card,
			isExpired: isCardExpired(card.expires),
		};
	});

	return {
		isFollowing: !!data.selectedEventInfo.followsGroup,
		paymentCards: cards,
	};
};

const getFirstAndLastName = (name: string): UserName => {
	const fullNameArr = name.split(' ');
	const firstName = fullNameArr.shift() || '';
	const lastName = fullNameArr.join(' ');
	return {
		firstname: firstName,
		lastname: lastName || firstName, // If last name is empty, use first name to instead
	};
};

export const generateBuyerInfo = (userProfile: FormattedUserProfile | null, isFollowOrganizer: boolean, isSaveCard: boolean):Partial<CheckoutsOrdersRequest> => {
	const { firstname, lastname } = getFirstAndLastName(userProfile?.name || '');
	return {
		buyerInfo: {
			firstname,
			lastname: lastname || '-',
			email: userProfile?.email || '',
		},
		preferences: {
			anonymous: false,
			joinPod: isFollowOrganizer,
			saveCard: isSaveCard,
			followOrganizer: isFollowOrganizer,
		},
	};
};